import React, { useState } from 'react'
import { BrandState } from 'state/brands'
import styled from 'particles/themes/styled'
import { MODAL_TYPE } from 'components/Team/common/Modals'
import CreateBrand from 'components/Team/common/Modals/CreateBrand'
import { usePermission } from 'hooks/permissions'
import { Brand as BrandPermission } from 'components/Shared/Permission/enums'
import IconButton from 'particles/Button/IconButton'
import Text from 'particles/Text'
import { useTranslation } from 'react-i18next'
import { useField } from 'formik'

const BrandCardContainer = styled.div`
    width: 100%;
    max-width: 770px;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 132px 1fr;
    min-height: 156px;
    gap: ${({ theme }) => theme.spacing[600]};
    padding: 12px;
    .brand-logo {
        width: 132px;
        height: 132px;
        align-self: center;
        object-fit: contain;
    }
    .empty-brand-logo {
        background-color: ${({ theme }) => theme.colors.surface.actionPastelHover};
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }
    .brand-details {
        display: flex;
        flex-direction: column;
        width: 100%;
        text-overflow: ellipsis;
        overflow: hidden;
        .title {
            text-overflow: ellipsis;
            overflow: hidden;
            max-width: 100%;
        }
        .brand-title-block {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .description {
            text-overflow: ellipsis;
            overflow: hidden;
            max-width: 100%;
            word-wrap: break-word;
        }
    }
`

interface BrandCardProps {
    selectedBrand: BrandState | null
    onBrandUpdateSuccess?: () => void
}

const BrandCard: React.FC<BrandCardProps> = ({ selectedBrand, onBrandUpdateSuccess }) => {
    const [t] = useTranslation()
    const [openModal, setOpenModal] = useState(false)
    const isCreateEditBrandPermitted = usePermission(BrandPermission.CreateEdit)
    const [, , helpers] = useField('brandUuid')

    if (!selectedBrand) return null
    return (
        <>
            <BrandCardContainer>
                <div>
                    {selectedBrand?.logo_image?.image ? (
                        <img className="brand-logo" src={selectedBrand?.logo_image?.image} />
                    ) : (
                        <div className="empty-brand-logo brand-logo">
                            <Text scale="H4" color="action">
                                {t('campaignEditor.setUpCampaign.enterBrandImagePlaceholder')}
                            </Text>
                        </div>
                    )}
                </div>
                <div className="brand-details">
                    <div className="brand-title-block">
                        <Text className="title" scale="bodyS700">
                            {selectedBrand.name}
                        </Text>
                        {isCreateEditBrandPermitted && (
                            <IconButton
                                type="button"
                                icon="pencilLine"
                                form="icon"
                                size="xs"
                                onClick={e => {
                                    e.stopPropagation()
                                    setOpenModal(true)
                                }}
                            />
                        )}
                    </div>
                    <Text className="description">{selectedBrand.description}</Text>
                </div>
            </BrandCardContainer>
            {openModal && (
                <CreateBrand
                    brand={selectedBrand}
                    type={MODAL_TYPE.EDIT_BRAND}
                    onUpdateSuccess={onBrandUpdateSuccess}
                    onClose={(closeOnSubmit: boolean) => {
                        setOpenModal(false)
                        if (closeOnSubmit) {
                            helpers.setError('')
                        }
                    }}
                />
            )}
        </>
    )
}

export default BrandCard
